<script setup lang="ts">
import { onMounted, ref, watch, computed, onUnmounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
//Components
import ToolBar from './annotator/ToolBar.vue'
import DroneSpinner from 'Common/components/elements/feedback/DroneSpinner.vue'
import MeasurementPopoverMenu from './MeasurementPopoverMenu.vue'
import DiscrepancyPopoverLabel from './DiscrepancyPopoverLabel.vue'
//Store
import { useImageReviewStore } from 'Store/pages/imageReview/imageReviewStore'
import { useMarkerStore } from 'Store/pages/imageReview/markerStore'
import { useFilterStore } from 'Store/filterStore'
//Utils
import { useImageAnnotator } from './useImageAnnotator'
import { isEqual } from 'lodash'
import { applyMatrix } from 'Common/utils/markerArea/helperFunctions'
import { calculateBoundingBox } from 'Common/utils/calculateBoundingBox'
import { generateMarkerStateFromAnnotations, generateMarkerStateFromMeasurements } from './helperFunctions'
import {
  markerCreatedCallback,
  markerSelectedCallback,
  markerDeselectedCallback,
  markerMouseEnteredCallback,
  markerMouseLeaveCallback,
  markerEditCallback,
} from 'Modules/pages/imageReview/markerEventHandlers'
//Types
import type { Ref } from 'vue'
import { ImageAnnotation } from 'Modules/types/pages/imageReview/imageAnnotationType'
import { ImageMeasurementType, ImageMeasurements } from 'Modules/types/pages/imageReview/imageMeasurementType'
import { AnnotatorModes } from 'Modules/types/pages/imageReview/enums'
import { MarkerTypes } from 'Modules/types/pages/imageReview/enums'
import { MarkerModes } from 'Common/utils/markerArea/types'

const store = useImageReviewStore()
const filterStore = useFilterStore()
const { annotations, annotationsData, stagedAnnotations } = storeToRefs(store.annotationsStore)
const { annotationsLoaded } = storeToRefs(store.serverStore)
const { createImageAnnotation, createImageMeasurement, deleteImageMeasurement } = store.serverStore
const { currentImage, currentImageLoaded } = storeToRefs(store.imagesStore)
const { currentMeasurement, currentHoveredMeasurement, measurements } = storeToRefs(store.measurementsStore)
const { removeMeasurement, setCurrentMeasurement, setCurrentHoveredMeasurement, removeTempMeasurement } =
  store.measurementsStore
const { setCurrentImageLoaded } = store.imagesStore

const markerStore = useMarkerStore()
const { createMarkerEntry, clearMarkerEntries } = markerStore

// --- Performance & Loading State ---
let loadRequestId = 0
let transformAnimationFrame: number | null = null
// Session ID ensures we stop rendering chunks if the user switches images mid-load
let renderSessionId = 0

const renderedCount = ref(0)
const totalToRender = ref(0)
const isRenderingAnnotations = ref(false)

const annotator = useImageAnnotator({
  onMarkerCreated: (markerEvent) => {
    markerCreatedCallback(markerEvent)
  },
  onMarkerEdit: markerEditCallback,
  onMarkerSelect: markerSelectedCallback,
  onMarkerDeselect: markerDeselectedCallback,
  onMarkerMouseEnter: markerMouseEnteredCallback,
  onMarkerMouseLeave: markerMouseLeaveCallback,
  isMarkersInteractive: true,
})

const imgRef = ref<HTMLImageElement | null>(null)
const imageSourceForTemplate = ref<string | null>(null)

const popoverLocationX = ref<number>(0)
const popoverLocationY = ref<number>(0)
const popoverTranslationLocation: Ref<'top' | 'bottom'> = ref('top')

const noImageMessage = computed<string>(() => {
  return currentImage.value ? 'Loading Image' : 'No Images to View'
})

// UPDATE: Only block global loading spinner on IMAGE load.
// Annotations can load in the background while the image is visible.
const displayLoading = computed<boolean>(() => {
  return !(currentImage.value && currentImageLoaded.value)
})

const renderProgress = computed(() => {
  if (totalToRender.value === 0) return 0
  return Math.round((renderedCount.value / totalToRender.value) * 100)
})

const imagesFilter = computed<Array<string>>(() => {
  return filterStore.filterState.image
})

/**
 * Called when the hidden <picture> tag finishes loading.
 * We extract the browser-selected URL (AVIF/JPG) and switch to the interactive view.
 */
function onLoaderImageLoad(event: Event): void {
  const imgElement = event.target as HTMLImageElement
  if (!imgElement) return

  const resolvedUrl = imgElement.currentSrc

  if (resolvedUrl) {
    imageSourceForTemplate.value = resolvedUrl
  }

  setCurrentImageLoaded(true)

  nextTick(() => {
    if (imgRef.value) {
      annotator.setImage(imgRef.value)
    }
  })
}

/**
 * Handle cases where the browser fails to load the image in the loader
 */
function onLoaderImageError() {
  if (currentImage.value?.imageUrl) {
    // If AVIF failed, force fallback to basic URL
    imageSourceForTemplate.value = currentImage.value.imageUrl
    // Allow the <img> tag to try loading this fallback
    setCurrentImageLoaded(true)
    nextTick(() => {
      if (imgRef.value) annotator.setImage(imgRef.value)
    })
  }
}

const resetLocalState = async () => {
  renderSessionId++ // Invalidate any running render loops
  isRenderingAnnotations.value = false
  renderedCount.value = 0
  totalToRender.value = 0

  annotator.reset()
  clearMarkerEntries()
  setCurrentImageLoaded(false)
}

// Helper for chunking
const waitFrame = () => new Promise((resolve) => requestAnimationFrame(resolve))

/**
 * Async Batch Rendering
 * Loads markers in chunks to keep the UI responsive and update the progress bar.
 */
async function loadAnnotationMarkers(annotationsList: Array<ImageAnnotation>) {
  const currentSession = renderSessionId

  // Set up loading state visible to UI
  isRenderingAnnotations.value = true
  // Note: We are adding to the existing count if this is an incremental update (like filtering)
  // But usually, for initial load, we might want to reset.
  // For filtering safety, we track the batch size specifically.
  const markersState = generateMarkerStateFromAnnotations(annotationsList)

  // Update total tracking for this specific batch
  const batchTotal = markersState.length
  // If this is a huge initial load, set the total global; otherwise add to it
  if (renderedCount.value === 0) {
    totalToRender.value = batchTotal
  } else {
    totalToRender.value += batchTotal
  }

  const CHUNK_SIZE = 50

  for (let i = 0; i < markersState.length; i += CHUNK_SIZE) {
    // If user switched images, stop rendering
    if (currentSession !== renderSessionId) {
      isRenderingAnnotations.value = false
      return
    }

    const chunk = markersState.slice(i, i + CHUNK_SIZE)

    chunk.forEach((state) => {
      const marker = annotator.markerArea.loadMarker(state)
      createMarkerEntry(marker.state.meta?.id, marker)
    })

    // Update progress
    renderedCount.value += chunk.length

    // Yield to main thread to allow UI/Spinner update
    await waitFrame()
  }

  // Final render to canvas
  if (currentSession === renderSessionId) {
    annotator.markerArea.render()
    // Small delay to let the progress bar show 100% briefly before hiding
    setTimeout(() => {
      if (currentSession === renderSessionId) isRenderingAnnotations.value = false
    }, 500)
  }
}

function loadMeasurements(measurements: ImageMeasurements) {
  //TODO improve marker loading logic - create better integration for using alongside markers store
  const markersState = generateMarkerStateFromMeasurements(measurements)
  markersState.forEach((state) => {
    const marker = annotator.markerArea.loadMarker(state)
    createMarkerEntry(marker.state.meta?.id, marker)
  })
}

function updatePopoverPosition(currentMeasurement: ImageMeasurementType) {
  const transformMatrix = annotator.markerArea.transformMatrix

  const startX = currentMeasurement.points[0][0]
  const startY = currentMeasurement.points[0][1]
  const endX = currentMeasurement.points[1][0]
  const endY = currentMeasurement.points[1][1]

  const scaledStart = applyMatrix(startX, startY, transformMatrix)
  const scaledEnd = applyMatrix(endX, endY, transformMatrix)

  const boundingBox = calculateBoundingBox([
    [scaledStart.x, scaledStart.y],
    [scaledEnd.x, scaledEnd.y],
  ])

  const centerX = (boundingBox.left + boundingBox.right) / 2
  const centerY = boundingBox.top

  popoverLocationX.value = centerX
  popoverLocationY.value = centerY
  popoverTranslationLocation.value = 'top'
}

function removeMeasurementMarker(measurement: ImageMeasurementType) {
  removeMeasurement(measurement)
  setCurrentMeasurement()
  const matchingMarker = annotator.markerArea.markers.find((marker) => {
    return marker.state.meta?.id === measurement.measurementUuid
  })
  annotator.removeMarker(matchingMarker)
  annotator.setMode(AnnotatorModes.Select)
}

/**
 * Encapsulates the logic to preload an image and set the source.
 */
const loadImage = (image: { imageUrl: string; avifImageUrl?: string } | null) => {
  // We initialize with the base URL, but we will let the <picture> tag resolve the AVIF
  if (image?.imageUrl) {
    imageSourceForTemplate.value = image.imageUrl
  } else {
    imageSourceForTemplate.value = null
  }
}

watch(currentImage, async (newImage, oldImage): Promise<void> => {
  if (newImage?.imageUuid === oldImage?.imageUuid) return

  await resetLocalState()
  loadImage(newImage)
})

async function processAndDrawMarkers() {
  if (annotationsLoaded.value !== true || annotator.ready.value !== true) {
    return
  }

  const markers = annotator.markerArea.markers

  const annotationIds = new Set(annotations.value.map((a) => a.annotationUuid))
  const stagedIds = new Set<string>()

  stagedAnnotations.value.forEach((annotation) => {
    if (annotation.annotationUuid) stagedIds.add(annotation.annotationUuid)
    if (annotation.tempAnnotationUuid) stagedIds.add(annotation.tempAnnotationUuid)
  })

  // Deletions
  const pendingDeletion = markers.filter((marker) => {
    const markerId = marker.state.meta?.id
    const isInData = annotationIds.has(markerId)
    const isStaged = stagedIds.has(markerId)

    const isSpecialCase =
      marker.typeName === MarkerTypes.Selection ||
      marker.state.mode === MarkerModes.Create ||
      marker.state.meta?.isMeasurement === true

    return !isInData && !isStaged && !isSpecialCase
  })

  if (pendingDeletion.length > 0) {
    pendingDeletion.forEach((marker) => annotator.deleteMarker(marker))
    // Update counts if we are removing
    if (renderedCount.value > 0) renderedCount.value -= pendingDeletion.length
  }

  // Creations
  const markerIds = new Set(markers.map((marker) => marker.state.meta?.id))

  const pendingCreation = annotations.value.filter((annotation) => {
    return !markerIds.has(annotation.annotationUuid)
  })

  if (pendingCreation.length > 0) {
    // Await the chunked loader so the UI knows we are busy
    await loadAnnotationMarkers(pendingCreation)
  }
}

watch([annotations, annotator.ready], processAndDrawMarkers, { deep: true })

watch([annotator.ready, measurements, imagesFilter], () => {
  const measurementsExist = measurements.value.length > 0
  const isMeasurementsFiltered = imagesFilter.value?.includes('measurements')
  const shouldPopulateMeasurements = annotator.ready.value && measurementsExist
  if (shouldPopulateMeasurements && !isMeasurementsFiltered) {
    const markers = annotator.markerArea.markers
    const markersPopulated = markers.filter((marker) => marker.state.meta?.isMeasurement === true).length !== 0
    if (!markersPopulated) loadMeasurements(measurements.value)
  }
  //TODO replace with toggling markers to hidden state
  //See ticket for additional details: https://gitlab.com/near-earth/inspection/aci-portfolio/jeteye-review/-/issues/225
  if (isMeasurementsFiltered) {
    const measurementMarkersToRemove = annotator.markerArea.markers.filter(
      (marker) => marker.state.meta?.isMeasurement === true
    )
    measurementMarkersToRemove.forEach((marker) => annotator.removeMarker(marker))
  }
})

watch([annotator.mode, annotator.markerType], () => {
  if (annotator.mode.value === AnnotatorModes.Create && annotator.markerType.value !== MarkerTypes.Measurement) {
    setCurrentMeasurement()
    setCurrentHoveredMeasurement()
    removeTempMeasurement()
  }
})

watch(currentMeasurement, (newValue) => {
  const matchingMarker = annotator.markerArea.markers.find(
    (marker) => marker.state.meta?.id === currentMeasurement.value?.measurementUuid
  )

  if (matchingMarker) {
    const measurementPoints = currentMeasurement.value?.points
    const markerPoints = matchingMarker.state?.anchorPoints?.map((anchorPoint) => [anchorPoint.x, anchorPoint.y])
    const pointsEqual = isEqual(measurementPoints, markerPoints)

    if (!pointsEqual && measurementPoints && markerPoints) {
      const anchorPoints = currentMeasurement.value.points.map((pointArray) => ({
        x: pointArray[0],
        y: pointArray[1],
      }))
      matchingMarker.state.anchorPoints = Array.from(anchorPoints)
      matchingMarker.state.handles = []
      matchingMarker.state.controlPoints = []
      matchingMarker.handles.populateHandles()
    }
  }

  if (newValue && newValue?.points && annotator.ready.value === true) {
    updatePopoverPosition(currentMeasurement.value)
  }
})

watch(currentHoveredMeasurement, (newValue) => {
  if (newValue && newValue?.points && annotator.ready.value === true) {
    updatePopoverPosition(currentHoveredMeasurement.value)
  }
})

watch([() => annotationsData.value], () => {
  annotationsData.value.forEach((annotation) => {
    if (annotation.pendingDeletion) {
      annotator.markerArea.markers
        .filter(
          (marker) =>
            marker.state.meta.id === annotation.annotationUuid ||
            marker.state.meta.annotationUuid === annotation.annotationUuid
        )
        .forEach((marker) => annotator.deleteMarker(marker))
    }
  })
})

watch(
  () => stagedAnnotations.value,
  (newValue, oldValue) => {
    oldValue.forEach((annotation) => {
      const existsInStagedAnnotations =
        newValue.find((_annotation) => _annotation.annotationUuid === annotation.annotationUuid) !== undefined
      if (!createImageAnnotation.isLoading && annotation.isTemp && !existsInStagedAnnotations) {
        const correspondingMarker = annotator.markerArea.markers.find(
          (marker) => marker.state.meta.id === annotation.annotationUuid
        )
        annotator.deleteMarker(correspondingMarker)
      }
    })
  }
)

watch([createImageMeasurement], (val) => {
  const mutation = val[0]
  const data = mutation.data
  const variables = mutation.variables
  const tempUuid = variables.measurementValues.measurementUuid
  const annotationUuid = variables.annotationUuid
  const markers = annotator.markerArea.markers
  const tempMarker = markers?.find((marker) => marker.state.meta?.id === tempUuid)
  const defectMarker = annotationUuid ? markers?.find((marker) => marker.state.meta?.id === annotationUuid) : null
  const isLoading = mutation.isLoading
  const isSuccess = mutation.isSuccess

  if (tempMarker) {
    if (isLoading) tempMarker.state.isSelectionLocked = false
    if (isSuccess) {
      const newUuid = data.measurementUuid //TODO resolve typing
      tempMarker.state.meta.id = newUuid
      annotator.setMode(AnnotatorModes.Select)
    }
  }
  if (defectMarker) {
    //Deselecting the associated defect marker is needed due to how markerarea currently handles
    //marker events, only allowing a single marker to be the "current" marker.
    //If a marker is not the current marker it does not propogate the majority events.
    //The user has to manually associated defect marker by clicking outside it's area in order to be
    //able to interact with it again.

    //Once and if marker area is updated to allow multiple marker interaction - this issue should be addressed.
    //Where the user can interact with any marker on the screen regardless of it's selection state.
    //This work is scoped as the latter part of AMS measurement project.
    defectMarker.controller.deselectMarker()
  }
})

watch([deleteImageMeasurement], (val) => {
  const mutation = val[0]
  const variables = mutation.variables
  const measurementValues = variables.measurementValues
  const isSuccess = mutation.isSuccess
  const measurementUuid = measurementValues.measurementUuid
  const marker = annotator.markerArea.markers?.find((marker) => marker.state.meta?.id === measurementUuid)
  if (isSuccess) {
    annotator.removeMarker(marker)
    if (currentMeasurement.value?.measurementUuid === measurementUuid) setCurrentMeasurement()
    if (currentHoveredMeasurement.value?.measurementUuid === measurementUuid) setCurrentHoveredMeasurement()
  }
})

watch([annotator.tranform], () => {
  if (!currentMeasurement.value) return
  if (transformAnimationFrame) return
  transformAnimationFrame = requestAnimationFrame(() => {
    if (currentMeasurement.value) {
      updatePopoverPosition(currentMeasurement.value)
    }
    transformAnimationFrame = null
  })
})

onMounted(() => {
  if (currentImage.value) {
    loadImage(currentImage.value)
  } else if (!currentImageLoaded.value) {
    resetLocalState()
  }
})

onUnmounted(() => {
  renderSessionId++ // Kill any pending chunks
  if (annotator && typeof annotator.close === 'function') {
    annotator.close()
  }
  if (transformAnimationFrame) {
    cancelAnimationFrame(transformAnimationFrame)
  }
  imgRef.value = null
  imageSourceForTemplate.value = null
})
</script>

<template>
  <div class="relative h-full w-full grid grid-rows-[2.5em_calc(100%-2.5em)]">
    <template v-if="displayLoading">
      <div
        id="inspectionImg"
        class="absolute top-0 h-full w-full text-white bg-neutral-900/80 flex flex-col items-center justify-center px-1 pb-5 z-10"
      >
        <DroneSpinner v-if="!currentImageLoaded" />
        {{ noImageMessage }}
      </div>
    </template>

    <Transition name="fade">
      <div
        v-if="currentImageLoaded && (isRenderingAnnotations || renderedCount < totalToRender)"
        class="absolute bottom-6 right-6 z-40 flex items-center gap-3 bg-black/80 backdrop-blur-md p-3 rounded-xl border border-white/10 shadow-2xl"
      >
        <div class="relative flex items-center justify-center">
          <svg class="w-10 h-10 transform -rotate-90">
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="currentColor"
              stroke-width="3"
              fill="transparent"
              class="text-white/10"
            />
            <circle
              cx="20"
              cy="20"
              r="16"
              stroke="currentColor"
              stroke-width="3"
              fill="transparent"
              :stroke-dasharray="2 * Math.PI * 16"
              :stroke-dashoffset="totalToRender > 0 ? 2 * Math.PI * 16 * (1 - renderProgress / 100) : 0"
              class="text-blue-500 transition-all duration-300"
            />
          </svg>
          <span v-if="totalToRender > 0" class="absolute text-[9px] font-bold text-white">{{ renderProgress }}%</span>
        </div>
        <div class="text-white leading-tight">
          <p class="text-xs font-bold">{{ totalToRender === 0 ? 'Analyzing...' : 'Loading Annotations' }}</p>
          <p class="text-[10px] text-neutral-400">{{ renderedCount }} / {{ totalToRender }} items</p>
        </div>
      </div>
    </Transition>

    <div class="relative h-full w-full z-10">
      <ToolBar
        :mode="annotator.mode.value"
        :set-mode="annotator.setMode"
        :set-type="annotator.createNewMarker"
        :set-zoom-level="annotator.setScale"
        :zoom-level="annotator.scale.value"
        :selected-marker-type="annotator.markerType.value"
      />
    </div>
    <div id="viewer-container" class="relative h-full w-full flex items-center justify-center overflow-hidden">
      <MeasurementPopoverMenu
        :location-x="popoverLocationX"
        :location-y="popoverLocationY"
        :translation-location="popoverTranslationLocation"
        :remove-measurement="removeMeasurementMarker"
      />
      <DiscrepancyPopoverLabel :annotations="stagedAnnotations" :tranform-matrix="annotator.tranform" />

      <div class="relative h-full w-full flex items-center justify-center">
        <img
          v-if="currentImageLoaded && imageSourceForTemplate"
          id="currentImage"
          ref="imgRef"
          class="max-h-full max-w-full object-contain"
          :src="imageSourceForTemplate"
        />

        <picture v-else-if="imageSourceForTemplate" class="h-full w-auto block opacity-0 pointer-events-none absolute">
          <source v-if="currentImage?.avifImageUrl" :srcset="currentImage.avifImageUrl" type="image/avif" />
          <img
            class="h-full w-auto"
            :src="imageSourceForTemplate"
            @load="onLoaderImageLoad"
            @error="onLoaderImageError"
          />
        </picture>
      </div>
    </div>
    <div ref="canvasContainer" class="h-full w-full"></div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
